import React, { useState } from "react";
import {
  SwipeableDrawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Collections as CollectionsIcon,
  CardGiftcard as GifBoxIcon,
  Person2 as UsersIcon,
  ArrowBack as ArrowBackIcon,
  VpnKey as VpnKeyIcon,
} from "@mui/icons-material";
import NFTCollectionsSection from "./Profile/NFTCollectionsSection";
import UserDetailsSection from "./Profile/UserDetailsSection";
import SecuritySection from "./Profile/SecuritySection";
import ContactsSection from "./Profile/ContactsSection";

import { Logout as LogoutIcon } from "@mui/icons-material";
import GiftsSection from "./Profile/GiftsSection";
import PersonalAccessTokenManagement from "./Profile/PersonalAccessTokenManagement";

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
}

type ProfileSection =
  | "details"
  | "security"
  | "nfts"
  | "gifts"
  | "contacts"
  | "accessTokens";

const ProfileDialog: React.FC<ProfileDialogProps> = ({
  open,
  onClose,
  onLogout,
}) => {
  const [activeSection, setActiveSection] = useState<ProfileSection | null>(
    null,
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case "details":
        return <UserDetailsSection />;
      case "security":
        return <SecuritySection />;
      case "nfts":
        return <NFTCollectionsSection />;
      case "gifts":
        return <GiftsSection />;
      case "contacts":
        return <ContactsSection />;
      case "accessTokens":
        return <PersonalAccessTokenManagement />;
      default:
        return (
          <>
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h6">Profile Menu</Typography>
            </Box>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setActiveSection("details")}>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary="User Details@" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setActiveSection("security")}>
                  <ListItemIcon>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText primary="Security" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setActiveSection("contacts")}>
                  <ListItemIcon>
                    <UsersIcon />
                  </ListItemIcon>
                  <ListItemText primary="Contacts" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setActiveSection("nfts")}>
                  <ListItemIcon>
                    <CollectionsIcon />
                  </ListItemIcon>
                  <ListItemText primary="NFT Collections" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setActiveSection("gifts")}>
                  <ListItemIcon>
                    <GifBoxIcon />
                  </ListItemIcon>
                  <ListItemText primary="Gifts" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => setActiveSection("accessTokens")}
                >
                  <ListItemIcon>
                    <VpnKeyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Access Tokens" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={onLogout}>
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign out" />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        );
    }
  };

  const handleBack = () => {
    setActiveSection(null);
  };

  const getSectionTitle = () => {
    switch (activeSection) {
      case "details":
        return "User Details";
      case "security":
        return "Security Settings";
      case "nfts":
        return "NFT Collections";
      case "gifts":
        return "Gift Cards";
      case "contacts":
        return "Contacts";
      default:
        return "Profile";
    }
  };

  return (
    <SwipeableDrawer
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      anchor="right"
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 400 },
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ width: "100%" }}>
        {activeSection ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">{getSectionTitle()}</Typography>
            </Box>
            <Box sx={{ p: 2, width: "100%" }}>{renderSectionContent()}</Box>
          </>
        ) : (
          renderSectionContent()
        )}
      </Box>
    </SwipeableDrawer>
  );
};

export default ProfileDialog;
